import http from '../shared/HttpCommon';
import { Dispatch, SetStateAction } from 'react';
import { CanceledError } from 'axios';
import { IScoreEntry, IScoreEntryFull } from '../shared/interfaces/IScoreEntry';
import WidgetService from './WidgetService';
import { IReviewEntry } from '../shared/interfaces/IReviewEntry';

const getLastScores = async (
  platform: string,
  company: string,
  loading: Dispatch<SetStateAction<boolean>> | undefined,
  signal: AbortSignal,
  setStatus?: (status: boolean) => void
) => {
  let data: IScoreEntry[] = [];
  let hasErrors = false;
  const safeSetStatus = (status: boolean) => setStatus?.(status);

  const fetchScore = async (kind?: string) => {
    try {
      const response = await http.post(
        `/${platform}/scores/last`,
        {
          company,
          ...(kind && { kind }),
        },
        { signal }
      );
      data.push(response.data);
    } catch (error) {
      hasErrors = true;
      if (!(error instanceof CanceledError)) {
        console.error(
          `Company "${company}", platform "${platform}" could not be displayed due to an error: ` +
            error
        );
      }
    }
  };

  try {
    loading?.(true);

    if (platform === 'kununu') {
      // The employee score kind was retired in 769968-A: kind "total" IS the employee
      // score from 2026-09-01, and the employee badge count now comes from the
      // monitoring entry (sourced from the total row's total_employee). Keeping the
      // fetch would serve the last employee row ever written - a frozen number - and
      // costs a third round-trip per kununu monitoring.
      const kinds = ['applicant', 'total'];
      for (const kind of kinds) {
        await fetchScore(kind);
      }
    } else {
      await fetchScore();
    }
  } catch (error) {
    hasErrors = true;
    if (!(error instanceof CanceledError)) {
      console.error(
        `Company "${company}", platform "${platform}" could not be displayed due to an error: ` +
          error
      );
    }
  } finally {
    loading?.(false);
  }

  safeSetStatus(!hasErrors);
  return data;
};

const getScores = async (
  platform: string,
  company: string,
  kind: 'total' | 'applicant',
  mode: FilterMode,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.post(
      `/${platform}/scores/filter`,
      { company, kind, mode },
      { signal }
    );
    loading(false);
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      loading(false);
      console.error(
        `Could not fetch scores for company "${company}", platform "${platform}": ` + error
      );
    }
  }
};

const getAllScores = async (
  platform: string,
  company: string,
  kind: 'total' | 'applicant',
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  const limit = 30;
  try {
    loading(true);
    const { data } = await http.post(`/${platform}/scores`, { company, limit, kind }, { signal });
    loading(false);
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      loading(false);
      console.error(
        `Could not fetch scores for company "${company}", platform "${platform}": ` + error
      );
    }
  }
};

const getAverageScore = async (
  platforms: string[],
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  const scores = [];
  return {};
};

const getLastTotalScore = async (
  platform: string,
  company: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
): Promise<IScoreEntry | undefined> => {
  try {
    loading(true);
    const response = await http.post(
      `/${platform}/scores/last`,
      {
        company,
        ...(platform === 'kununu' && { kind: 'total' }),
      },
      { signal }
    );
    return response.data; // TODO: response.data zurückgeben
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(
        `Could not fetch score for company "${company}", platform "${platform}": ` + error
      );
    }
  } finally {
    loading(false);
  }
};

const ScoreService = {
  getLastScores,
  getLastTotalScore,
  getScores,
  getAllScores,
  getAverageScore,
};

export default ScoreService;
